import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import { 
  Upload, 
  Download,
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Trash2,
  ChevronRight,
  Loader2,
  RefreshCw,
  FileSpreadsheet,
  Thermometer
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status step configuration
const STATUS_STEPS = [
  { key: 'RECEIVED', label: 'Received', icon: Upload },
  { key: 'PROCESSING', label: 'Processing', icon: Clock },
  { key: 'APPROVED_INSTALLED', label: 'Approved & Installed', icon: CheckCircle2 }
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    RECEIVED: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Upload, label: 'Received' },
    PROCESSING: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Processing' },
    APPROVED_INSTALLED: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Approved & Installed' },
    REJECTED: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Rejected' }
  };
  
  const config = statusConfig[status] || statusConfig.RECEIVED;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function CustomTemperatureModelManager() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  
  // File upload ref and state
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: ''
  });

  const { token } = useAuth();
  const authToken = token;

  // Fetch user's custom temperature models
  const fetchModels = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/custom-temperature-models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(response.data || []);
    } catch (error) {
      console.error('Failed to fetch temperature models:', error);
      toast.error('Failed to load temperature models');
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchModels();
      setLoading(false);
    };
    loadData();
  }, [fetchModels]);

  // Download template
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/custom-temperature-models/template`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'temperature_profile_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Only Excel files (.xlsx, .xls) are allowed');
        return;
      }
      setSelectedFile(file);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!uploadForm.name.trim()) {
      toast.error('Please enter a model name');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a temperature file to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', uploadForm.name.trim());
      formData.append('description', uploadForm.description || '');
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${BACKEND_URL}/api/custom-temperature-models/upload`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      toast.success(response.data.message || 'Temperature model submitted for review');
      setShowUploadModal(false);
      setUploadForm({ name: '', description: '' });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      await fetchModels();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload temperature model';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  // Handle download file
  const handleDownloadFile = async (model) => {
    if (!model.file_name) {
      toast.error('No file attached to this model');
      return;
    }

    setDownloadingId(model.id);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/custom-temperature-models/${model.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', model.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!modelToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/custom-temperature-models/${modelToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Temperature model deleted');
      setShowDeleteModal(false);
      setModelToDelete(null);
      
      await fetchModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete model');
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Check if model can be deleted (not approved)
  const canDelete = (status) => {
    return status !== 'APPROVED_INSTALLED';
  };

  return (
    <Card className="border-slate-200 bg-gradient-to-br from-white to-orange-50/30" data-testid="custom-temperature-model-manager">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg text-orange-600 flex items-center gap-2">
              <Thermometer className="h-5 w-5" />
              Temperature Model Upload
            </CardTitle>
            <CardDescription className="text-gray-600 mt-1">
              Upload your own annual temperature profile as a daily dataset
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchModels}
              disabled={loading}
              data-testid="refresh-temp-models-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              data-testid="download-template-btn"
            >
              {downloadingTemplate ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Download Template
            </Button>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="upload-temp-model-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Model
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Info Box */}
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-orange-800">
              <p className="font-medium mb-1">How to upload your temperature profile:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Click "Download Template" to get the Excel template</li>
                <li>Fill in temperatures (°C) for each day from Jan 1 to Dec 31</li>
                <li>Upload your completed file for TPS review</li>
                <li>Once approved, your model will appear in Growth vs Model and Growth Prognosis</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Models Table */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
          </div>
        ) : models.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300">
            <Thermometer className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No temperature models uploaded yet</p>
            <p className="text-gray-400 text-xs mt-1">Download the template and upload your daily temperature profile</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="temp-models-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Model Name</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Year Type</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Submitted</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">File</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr 
                    key={model.id} 
                    className={`border-b border-slate-100 hover:bg-white/80 transition-colors ${
                      model.status === 'APPROVED_INSTALLED' ? 'bg-green-50/30' :
                      model.status === 'REJECTED' ? 'bg-red-50/30' : ''
                    }`}
                    data-testid={`temp-model-row-${model.id}`}
                  >
                    <td className="py-3 px-3">
                      <p className="font-medium text-gray-800">{model.name}</p>
                      {model.description && (
                        <p className="text-xs text-gray-500 truncate max-w-[200px]" title={model.description}>
                          {model.description}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3 text-gray-600 text-xs">
                      {model.year_type || '365-day'}
                    </td>
                    <td className="py-3 px-3 text-gray-500 text-xs">
                      {formatDate(model.submitted_at)}
                    </td>
                    <td className="py-3 px-3">
                      {model.file_name ? (
                        <div className="flex items-center gap-1">
                          <FileSpreadsheet className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-xs text-gray-600 truncate max-w-[100px]" title={model.file_name}>
                            {model.file_name}
                          </span>
                          {model.file_size && (
                            <span className="text-xs text-gray-400">({formatFileSize(model.file_size)})</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No file</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <StatusBadge status={model.status} />
                      {model.review_notes && (
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-[150px]" title={model.review_notes}>
                          Note: {model.review_notes}
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-center gap-1">
                        {model.file_name && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadFile(model)}
                            disabled={downloadingId === model.id}
                            className="h-8 w-8 p-0 text-gray-500 hover:text-orange-600"
                            title="Download file"
                            data-testid={`download-temp-model-${model.id}`}
                          >
                            {downloadingId === model.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        {canDelete(model.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setModelToDelete(model);
                              setShowDeleteModal(true);
                            }}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete model"
                            data-testid={`delete-temp-model-${model.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Status Progress Legend */}
        {models.length > 0 && (
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-medium text-gray-500 mb-2">Approval Workflow:</p>
            <div className="flex items-center gap-2 flex-wrap">
              {STATUS_STEPS.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex items-center">
                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-white border border-gray-200">
                      <Icon className="h-3 w-3 text-gray-500" />
                      <span className="text-xs text-gray-600">{step.label}</span>
                    </div>
                    {index < STATUS_STEPS.length - 1 && (
                      <ChevronRight className="h-3 w-3 mx-1 text-gray-300" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Help & Benefits Section */}
        <div className="mt-4">
          <HelpBenefits 
            youtubeUrl={HELP_URLS.temperatureModelUpload.youtube}
            benefitsUrl={HELP_URLS.temperatureModelUpload.benefits}
            featureName="Temperature Profile Upload"
          />
        </div>
      </CardContent>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={(open) => {
        setShowUploadModal(open);
        if (!open) {
          setSelectedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Thermometer className="h-5 w-5" />
              Upload Temperature Model
            </DialogTitle>
            <DialogDescription>
              Upload your completed temperature profile template (.xlsx) for TPS review and approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Model Name */}
            <div className="space-y-2">
              <Label>Model Name *</Label>
              <Input
                placeholder="e.g., My Farm Temperature Profile 2024"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="temp-model-name-input"
              />
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Temperature Template (.xlsx) *</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-orange-400 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="temp-file-upload"
                  data-testid="temp-file-upload-input"
                />
                <label
                  htmlFor="temp-file-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  {selectedFile ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <FileSpreadsheet className="h-8 w-8" />
                      <div>
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to select file</p>
                      <p className="text-xs text-gray-400">Only .xlsx or .xls files</p>
                    </>
                  )}
                </label>
              </div>
              {selectedFile && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-red-500 text-xs"
                >
                  Remove file
                </Button>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="e.g., Daily sea temperatures from my farm location..."
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                className="resize-none"
                rows={2}
                data-testid="temp-model-description-input"
              />
            </div>

            {/* Requirements Note */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-700">
                <p className="font-medium">Template Requirements:</p>
                <ul className="text-xs mt-1 list-disc list-inside space-y-0.5">
                  <li>Must cover Jan 1 - Dec 31 (365 days)</li>
                  <li>All temperature values required (no blanks)</li>
                  <li>Temperatures &gt; 30°C will be capped to 30°C</li>
                  <li>Date format: DD/MM/YY</li>
                </ul>
              </div>
            </div>

            {/* Info Note */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">TPS Review Required</p>
                <p className="text-xs mt-1">Your temperature model will be reviewed by the TPS team. Once approved and installed, it will become available for use in your analyses.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="submit-temp-model-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Model
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Temperature Model
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{modelToDelete?.name}"? This will also remove the uploaded file. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-temp-model-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Model'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
